import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { Outlet } from 'react-router';
import { HelpButton } from '../../guida/HelpButton';
import { ticketPosition } from './ticket-position';

/** Altezza della barra fissa in cima al pannello: un pulsante da 36px più 14px sopra e sotto, come nel prototipo. */
const DETAIL_BAR_HEIGHT = 64;

/** Una voce del percorso della shell: la lista da cui si è aperto il dettaglio. */
export interface DetailListCrumb {
  label: string;
  href: string;
}

/** Contesto che il contenitore passa alla rotta figlia del dettaglio (`TicketDetailPanel`). */
export interface TicketDetailOutletContext {
  /** Chiude il dettaglio e torna alla lista di provenienza. */
  onClose: () => void;
  /** Testo del ritorno alla lista: «Torna alla coda», «Torna all'archivio» (#123). */
  backLabel: string;
  /** La lista di provenienza nel percorso della shell: «Coda › EC-2026-0001». */
  crumb: DetailListCrumb;
}

interface TicketDetailDrawerProps extends TicketDetailOutletContext {
  /** Codice del ticket aperto, come arriva dall'URL (anche minuscolo). */
  code: string;
  /** Codici dei ticket della lista di provenienza (la pagina corrente), nell'ordine in cui li mostra. */
  codes: readonly string[];
}

/**
 * Contenitore del dettaglio del ticket (PAGE-002, FED-003 decisione 6, #106): `Drawer` temporaneo a destra,
 * largo `min(880px, 92vw)` e a tutta larghezza sotto `sm`, sopra la lista che resta montata sotto.
 * È modale: il focus resta dentro e il backdrop, Esc o il pulsante di ritorno lo chiudono.
 *
 * In cima una barra fissa con il ritorno, «Ticket N di M» e il «?» della Guida; sotto, su `grey.50`, la
 * rotta figlia (`<Outlet>`) con i blocchi in `MainCard`. Non conosce la coda: etichetta del ritorno,
 * percorso e lista di provenienza sono parametri, così l'archivio (#123) lo riusa con «Torna
 * all'archivio». Il ritorno del focus alla riga alla chiusura è della lista, che conosce le sue righe.
 */
export function TicketDetailDrawer({ code, codes, onClose, backLabel, crumb }: TicketDetailDrawerProps) {
  const position = ticketPosition(code, codes);
  return (
    <Drawer
      open
      anchor="right"
      onClose={onClose}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-label': 'Dettaglio del ticket',
          sx: {
            width: { xs: '100%', sm: 'min(880px, 92vw)' },
            bgcolor: 'grey.50',
            borderLeft: 1,
            borderColor: 'divider',
            // Una voce del thread raggiunta da un link (#70) non finisce sotto la barra fissa.
            scrollPaddingTop: DETAIL_BAR_HEIGHT + 16,
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          minHeight: DETAIL_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          px: { xs: 4, sm: 6 },
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {backLabel}
        </Button>
        <Box sx={{ flex: 1 }} />
        {position && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {position}
          </Typography>
        )}
        <HelpButton screen="/ticket/:codice" />
      </Box>
      <Outlet context={{ onClose, backLabel, crumb } satisfies TicketDetailOutletContext} />
    </Drawer>
  );
}
